// src/App.js
import React, { useEffect, useContext, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

import Layout from './components/Layout';
import TabsNavigation from './components/TabsNavigation';

import { LocationProvider } from './context/LocationContext';
import RequireLocation from './components/RequireLocation';
import { AuthProvider } from './context/AuthContext';
import { FilterProvider } from './context/FilterContext';
import { LikesProvider } from './context/LikesContext';
// Importamos ModalProvider, ModalContainer y TabProvider
import { ModalProvider } from './context/ModalContext';
import ModalContainer from './components/ModalContainer';
import { TabProvider } from './context/TabContext';
import { ModalHeaderProvider } from './context/ModalHeaderContext';

import { ModalContext } from './context/ModalContext';
import { TabContext } from './context/TabContext';
import { AuthContext } from './context/AuthContext';

import ModalPetDetail from './components/ModalPetDetail';
import ModalProfesionalDetail from './components/ModalProfesionalDetail';

import ActivationErrorPage from './pages/ActivationErrorPage';
import ActivationSuccessPage from './pages/ActivationSuccessPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

import PendingLikesHandler from './components/PendingLikesHandler';


function ModalSwitch() {
  const location = useLocation();
  const state = location.state || {};
  const { modalStacks, openModal } = useContext(ModalContext);
  const { activeTab } = useContext(TabContext);

  // Ref para recordar qué ruta (pathname + search) ya procesamos
  const openedRef = useRef({
    petPath: null,
    profPath: null,
  });

  useEffect(() => {
    // Solo seguimos si no hay otro background y la URL tiene ?modal=true
    if (!state.background && location.search.includes('modal=true')) {
      // ─── Caso “/pet/:id?modal=true” ──────────────────────────────────
      if (location.pathname.startsWith('/pet/')) {
        const id = location.pathname.split('/pet/')[1]; // ej. '2456'
        const petKey = `/pet/${id}`;

        // Si ya abrimos este petKey, no volvemos a hacerlo
        if (openedRef.current.petPath !== petKey) {
          // Y, adicionalmente, comprobamos en la pila que no haya ninguno idéntico
          if (
            !(
              modalStacks[activeTab] &&
              modalStacks[activeTab].find(m => m.metadata?.petId === id)
            )
          ) {
            openModal(
              <ModalPetDetail petId={id} />,
              { tab: activeTab, metadata: { petId: id } }
            );
          }
          openedRef.current.petPath = petKey;
        }
      }
      // ─── Caso “/professional/:id?modal=true” ───────────────────────────
      else if (location.pathname.startsWith('/professional/')) {
        const id = location.pathname.split('/professional/')[1];
        const profKey = `/professional/${id}`;

        if (openedRef.current.profPath !== profKey) {
          if (
            !(
              modalStacks[activeTab] &&
              modalStacks[activeTab].find(m => m.metadata?.profId === id)
            )
          ) {
            openModal(
              <ModalProfesionalDetail professionalId={id} />,
              { tab: activeTab, metadata: { profId: id } }
            );
          }
          openedRef.current.profPath = profKey;
        }
      }
    }

    // ─── Limpiar los flags cuando la ruta cambie a otra distinta ─────────
    // Si ya no estamos en /pet/:id, reseteamos petPath
    if (!location.pathname.startsWith('/pet/')) {
      openedRef.current.petPath = null;
    }
    // Si ya no estamos en /professional/:id, reseteamos profPath
    if (!location.pathname.startsWith('/professional/')) {
      openedRef.current.profPath = null;
    }

    // Este useEffect solo necesita dispararse cuando cambien:
    //  • location.search  (para detectar “modal=true”)
    //  • state.background (para distinguir modales internos vs carga directa)
    //  • activeTab        (para saber en qué pestaña abrir el modal)
    // No incluimos modalStacks aquí porque, gracias a openedRef,
    // nunca volveremos a “abrir” el mismo petPath/profPath dos veces.
  }, [location.search, location.pathname, state.background, activeTab, modalStacks, openModal]);

  // Modificación: si detectamos modal=true y no hay background, usamos la misma ruta
  // para que React Router mantenga el feed de fondo en esa URL.
  const background =
    state.background ||
    (location.search.includes('modal=true') ? { pathname: location.pathname } : null);

  return (
    <>
      {/* Rutas de fondo: Home y, si se accede directo, la misma ruta de detalle */}
      <Routes location={background || location}>
        <Route element={<Layout />}>
          <Route path="/" element={<TabsNavigation />} />
          {/* Rutas para detail pages como fondo */}
          <Route path="/pet/:id" element={<TabsNavigation />} />
          <Route path="/professional/:id" element={<TabsNavigation />} />

          {/* RUTAS DE ACTIVACIÓN */}
          <Route path="/activation-error" element={<ActivationErrorPage />} />
          <Route path="/activated" element={<ActivationSuccessPage />} />
          {/* → NUEVA RUTA para reset password en React */}
          <Route path="/reset-password/:uid/:token" element={<ResetPasswordPage />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  return (
    <BrowserRouter>
      <LocationProvider>
        {/** Hasta que `loading` sea false no renderizamos nada */}
        <RequireLocation>
          <FilterProvider>
            <ModalProvider>
              <ModalHeaderProvider>
                <AuthProvider>
                  <PendingLikesHandler />
                  <LikesProvider>
                    <TabProvider>
                      {/** Cada vez que cambie isProfessional, TabsNavigation se desmonta y remonta */}
                      <AuthContext.Consumer>
                        {({ user }) => (
                          <div key={user?.is_professional ? 'pro' : 'user'}>
                            <ModalSwitch />
                            <ModalContainer />
                          </div>
                        )}
                      </AuthContext.Consumer>
                    </TabProvider>
                  </LikesProvider>
                </AuthProvider>
              </ModalHeaderProvider>
            </ModalProvider>
          </FilterProvider>
        </RequireLocation>
      </LocationProvider>
    </BrowserRouter>
  );
}

export default App;
